"use client";

import type { CSSProperties } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { isString, mergeClasses } from "@huub-web/utils";

import {
  type ClassNameProp,
  type ColorProp,
  type RequiredChildrenProp,
  polymorphicForwardRef,
} from "../../props";

import type { TypographyVariant } from "../../theme/types/TypographyVariant";
import { Markdown } from "../Markdown";
import type { ColorName } from "../../theme/types/ColorName";

import {
  clampStyle,
  currentColor25Style,
  currentColor50Style,
  currentColor75Style,
  textRecipeStyle,
  textStyle,
} from "./Text.css";
import { typography } from "../../theme/css/typography.css";
import { colors } from "../../theme/css/colors.css";

type TextRecipeVariants = RecipeVariants<typeof textRecipeStyle>;

export type TextProps = RequiredChildrenProp &
  ClassNameProp &
  ColorProp<
    ColorName | "currentColor75" | "currentColor50" | "currentColor25"
  > &
  TextRecipeVariants & {
    variant?: TypographyVariant;
    ellipsis?: boolean;
    bold?: boolean;
    noWrap?: boolean;
    markdown?: boolean;
    italic?: boolean;
    clamp?: number;
    inline?: boolean;
    align?: "left" | "center" | "right" | "justify";
    grow?: boolean;
    shrink?: boolean;
  };

/**
 * A component for displaying text.
 */
export const Text = polymorphicForwardRef<"div", TextProps>(
  (
    {
      as: Element = "div",
      variant = "bodyM",
      className,
      ellipsis = false,
      italic = false,
      bold = false,
      inline = false,
      markdown = false,
      noWrap = false,
      color,
      clamp,
      align,
      shrink,
      strikeThrough = false,
      alignSelf,
      children,
      grow = false,
      ...props
    },
    ref,
  ) => (
    <Element
      ref={ref}
      className={mergeClasses(
        typography[variant],
        textStyle,
        textRecipeStyle({
          ellipsis,
          align,
          alignSelf,
          bold,
          italic,
          inline,
          noWrap,
          grow,
          shrink,
          strikeThrough,
        }),
        (clamp ?? 0) > 0 ? clampStyle : undefined,
        color === "currentColor50"
          ? currentColor50Style
          : color === "currentColor25"
            ? currentColor25Style
            : color === "currentColor75"
              ? currentColor75Style
              : color
                ? colors[color]
                : undefined,
        className,
      )}
      style={
        {
          "--clamp": (clamp ?? 0) > 0 ? clamp : undefined,
        } as CSSProperties
      }
      {...props}
    >
      {isString(children) && markdown ? (
        <Markdown content={children} />
      ) : (
        children
      )}
    </Element>
  ),
);
